import { useState, useEffect, useCallback, useRef } from 'react';
import { informeService } from '@/services/informeService';
import { InformeResponse } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { Categoria, Nivel, Competicion } from '@/types/enums';

/**
 * Interfaz para los filtros de "Tus Informes"
 */
export interface FiltrosTusInformes {
  fechaInicio?: string;
  fechaFin?: string;
  competicion?: Competicion;
  categoria?: Categoria;
  nivel?: Nivel;
  mostrarTodos?: boolean;
}

/**
 * Hook para gestionar los informes del entrenador autenticado con filtros y paginación
 */
export const useTusInformes = () => {
  const { user } = useAuth();
  const [informes, setInformes] = useState<InformeResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<FiltrosTusInformes>({});
  
  // Estados para paginación
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  
  // Referencia para evitar llamadas duplicadas en modo estricto de React o montajes rápidos
  const loadingRef = useRef(false);

  /**
   * Obtiene informes paginados del servidor
   * @param pageNumber Número de página a obtener
   * @param reset Si es true, reinicia la lista
   */
  const obtenerInformes = useCallback(async (pageNumber: number = 0, reset: boolean = false) => {
    if (!user?.id || loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const response = await informeService.getInformesPaginados({
        page: pageNumber,
        size: 10,
        autorId: user.id,
        // Filtros opcionales
        categoria: filtros.categoria,
        nivel: filtros.nivel,
        // TODO: Backend soporta fecha exacta o año, pero no rango directamente en Specification base. 
        // Si el backend no soporta rango createdAt, esto requeriría ajuste backend.
        // Asumiremos que el filtrado por fecha se hará si el backend lo permite, 
        // o por ahora simplificaremos ignorando rango si no está soportado.
        sortBy: 'createdAt',
        sortDir: 'desc'
      });
      
      if (response.success && response.data) {
        // Mapear la respuesta paginada correctamente según el DTO del backend
        const { informes: content, esUltimaPagina, totalElementos } = response.data as any;
        const nuevosInformes = content || [];
        setInformes(prev => reset ? nuevosInformes : [...(prev || []), ...nuevosInformes]);
        setHasMore(!esUltimaPagina);
        setTotalElements(totalElementos);
        setPage(pageNumber);
      } else {
        setError('Error al obtener informes');
      }
      
    } catch (err) {
      console.error('❌ Error en obtenerInformes:', err);
      setError('Error de conexión al obtener informes');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user?.id, filtros]);

  /**
   * Carga la siguiente página
   */
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      obtenerInformes(page + 1, false);
    }
  }, [loading, hasMore, page, obtenerInformes]);

  /**
   * Verifica si hay filtros activos
   */
  const hayFiltrosActivos = useCallback(() => {
    return Object.keys(filtros).some(key => 
      key !== 'mostrarTodos' && filtros[key as keyof FiltrosTusInformes] !== undefined && filtros[key as keyof FiltrosTusInformes] !== ''
    );
  }, [filtros]);

  /**
   * Actualiza los filtros y reinicia la búsqueda
   */
  const actualizarFiltros = useCallback((nuevosFiltros: Partial<FiltrosTusInformes>) => {
    setFiltros(prev => {
      const updated = { ...prev, ...nuevosFiltros };
      // Al cambiar filtros, forzamos recarga inmediata reseteando todo
      // Necesitamos un efecto secundario o llamar directamente a obtenerInformes aquí no es ideal por el estado asíncrono de setFiltros
      return updated;
    });
    // Resetear página se maneja en el useEffect que observa 'filtros'
  }, []);

  /**
   * Limpia todos los filtros
   */
  const limpiarFiltros = useCallback(() => {
    setFiltros({});
  }, []);

  /**
   * Actualiza un informe en la lista local
   */
  const actualizarInforme = async (id: number, data: Partial<InformeResponse>): Promise<InformeResponse> => {
    try {
      setLoading(true);
      setError(null);
      
      const formData = new FormData();
      
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'foto') {
          formData.append(key, String(value));
        }
      });
      
      if (data.foto && data.foto instanceof File) {
        formData.append('foto', data.foto);
      }
      
      const response = await informeService.actualizarInforme(id, formData);
      
      let informeActualizado: InformeResponse;
      
      if (response && response.success && response.data) {
          informeActualizado = response.data;
      } else {
        throw new Error(response.message || 'Error al actualizar informe');
      }
      
      setInformes(prev => 
        prev.map(informe => 
          informe.id === id ? informeActualizado : informe
        )
      );
      
      return informeActualizado;
      
    } catch (err) {
      setError('Error de conexión al actualizar informe');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar informes iniciales o cuando cambian los filtros/usuario
  useEffect(() => {
    // Resetear y cargar primera página
    if (user?.id) {
        setPage(0);
        setInformes([]);
        setHasMore(true);
        // Pequeño timeout para asegurar que el estado se limpie antes de cargar
        // o simplemente llamar con reset=true
        obtenerInformes(0, true);
    }
  }, [user?.id, filtros]); // Dependencia en filtros para recargar al cambiar

  return {
    informes,
    loading,
    error,
    filtros,
    loadMore,
    hasMore,
    actualizarFiltros,
    limpiarFiltros,
    actualizarInforme,
    totalInformes: totalElements,
    hayFiltrosActivos: hayFiltrosActivos(),
    // Compatibilidad con UI anterior
    informesFiltrados: informes ? informes.length : 0, 
    mostrandoLimitado: false,
    obtenerInformes: () => obtenerInformes(0, true) // Función para refrescar manualmente
  };
};